import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Upload, Download, FileSpreadsheet, Users, Briefcase, FileText, Receipt, Wrench, DollarSign, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const importTypes = [
  { id: 'clients', label: 'Clients', icon: Users, description: 'Import client/customer data' },
  { id: 'contractors', label: 'Contractors/Trades', icon: Wrench, description: 'Import trade contractors' },
  { id: 'jobs', label: 'Jobs', icon: Briefcase, description: 'Import job/project data' },
  { id: 'quotes', label: 'Quotes', icon: FileText, description: 'Import quote data with line items' },
  { id: 'invoices', label: 'Invoices', icon: Receipt, description: 'Import invoice data' },
  { id: 'pricing', label: 'Pricing Schedules', icon: DollarSign, description: 'Import pricing data' },
];

export const ImportDataPage = () => {
  const [selectedType, setSelectedType] = useState('clients');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/import/${selectedType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} ${selectedType}`);
      } else {
        toast.warning('No records were imported');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setResult({
        success: false,
        import_type: selectedType,
        total_rows: 0,
        imported: 0,
        skipped: 0,
        errors: [error.response?.data?.detail || 'Import failed']
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get(`/import/templates/${selectedType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const selectedTypeInfo = importTypes.find(t => t.id === selectedType);
  const Icon = selectedTypeInfo?.icon || FileSpreadsheet;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Import Data</h1>
        <p className="text-muted-foreground mt-1">
          Import data from Tradeify, Simpro, or other systems via CSV files
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Import Type Selection */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Select Data Type</CardTitle>
            <CardDescription>Choose what you want to import</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {importTypes.map((type) => {
              const TypeIcon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => { setSelectedType(type.id); setResult(null); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                    selectedType === type.id 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:bg-muted/50'
                  }`}
                  data-testid={`import-type-${type.id}`}
                >
                  <TypeIcon className={`h-5 w-5 ${selectedType === type.id ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div>
                    <div className={`font-medium text-sm ${selectedType === type.id ? 'text-primary' : ''}`}>
                      {type.label}
                    </div>
                    <div className="text-xs text-muted-foreground">{type.description}</div>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  Import {selectedTypeInfo?.label}
                </CardTitle>
                <CardDescription>{selectedTypeInfo?.description}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drag and Drop Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? 'border-primary bg-primary/5' : 'border-border'
              } ${importing ? 'opacity-50 pointer-events-none' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
                disabled={importing}
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className={`h-12 w-12 mx-auto mb-4 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className="text-lg font-medium mb-1">
                  {importing ? 'Importing...' : 'Drop your CSV file here'}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse
                </p>
                <Button variant="outline" disabled={importing} type="button">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Select CSV File
                </Button>
              </label>
            </div>

            {/* Import Result */}
            {result && (
              <div className={`rounded-lg p-4 ${result.imported > 0 ? 'bg-green-500/10 border border-green-500/20' : result.errors?.length > 0 ? 'bg-red-500/10 border border-red-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                <div className="flex items-start gap-3">
                  {result.imported > 0 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                  ) : result.errors?.length > 0 ? (
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">Import Results</h4>
                    <div className="grid grid-cols-3 gap-4 mb-3">
                      <div>
                        <div className="text-2xl font-bold">{result.total_rows}</div>
                        <div className="text-xs text-muted-foreground">Total Rows</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-500">{result.imported}</div>
                        <div className="text-xs text-muted-foreground">Imported</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-yellow-500">{result.skipped}</div>
                        <div className="text-xs text-muted-foreground">Skipped</div>
                      </div>
                    </div>
                    
                    {result.errors?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm font-medium mb-1">Errors/Warnings:</div>
                        <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                          {result.errors.slice(0, 10).map((error, i) => (
                            <div key={i} className="text-muted-foreground">{error}</div>
                          ))}
                          {result.errors.length > 10 && (
                            <div className="text-muted-foreground">...and {result.errors.length - 10} more</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h4 className="font-semibold text-sm mb-2">Tips for importing from other systems:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• <strong>Tradeify:</strong> Export your data as CSV from Settings → Data Export</li>
                <li>• <strong>Simpro:</strong> Use Reports → Export to CSV feature</li>
                <li>• <strong>Excel:</strong> Save as CSV (Comma delimited) format</li>
                <li>• Download the template to see the expected column format</li>
                <li>• Duplicates (same email) will be skipped automatically</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ImportDataPage;
